import React from 'react';
import {Text, Button, Icon} from '@/ui-kits';
import {Images, Icons} from '@/constants';
import {useSelector} from 'react-redux';
import {authDataSelector} from '@/services/redux/selectors';
import {formatName} from '@/utils/common_functions';
import {useNavigate} from 'react-router-dom';
import {Routes} from '@/navigation/Routes';
import {SpecialistStatistics} from './SpecialistStatistics';
import {StepCard} from './StepCard';


export const NoConsultationCard: React.FC = () => {
  const authData = useSelector(authDataSelector);
  const navigate = useNavigate();

  const handleViewConsultations = () => {
    navigate(Routes.CONSULTATION);
  };

  const handleGoToProfile = () => {
    navigate(Routes.PROFILE);
  };

  return (
    <div className="px-3 sm:px-4 md:px-6 py-4 flex flex-col gap-4 sm:gap-6">
      {/* Welcome Message */}
      <div className="flex flex-col gap-2">
        <Text variant="headingSemiBold" className="gradient-text text-2xl sm:text-3xl lg:text-4xl">
          Welcome, Dr. {formatName(authData?.username ?? '')}
        </Text>
        <Text variant="body" className="text-ui_input_border_dark text-sm sm:text-base lg:text-lg">
          You're all set up! Once consultations are assigned, you'll see them reflected here.
        </Text>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
        <SpecialistStatistics value={0} icon="diagnosis" title="Total Consultations" />
        <SpecialistStatistics value={0} icon="medical_record" title="Pending Opinions" />
        <SpecialistStatistics variant="dark" value="--" icon="star_big" title="Average Rating" />
      </div>

      {/* Learn How it Works Section */}
      <div className="bg-white rounded-2xl px-4 gap-0 sm:gap-4 flex flex-col sm:rounded-3xl py-4 mt-2 sm:mt-4 border border-gray-200 shadow-md">
        <Text variant="headingSemiBold" className="text-ui_blue text-center !text-lg sm:!text-xl md:!text-3xl mb-0 sm:mb-0">
          Learn How it Works
        </Text>

        {/* Mobile Image - Square Container */}
        <div className="w-[110%] -mx-[5%] sm:w-full sm:mx-0 overflow-hidden rounded-lg aspect-square sm:hidden -mt-2 -mb-3 sm:mt-0 sm:mb-0">
          <img 
            src={Images.learnHowItWorksResponsive} 
            alt="Learn how it works" 
            className="w-full h-full object-contain rounded-lg" 
          />
        </div>
        
        {/* Laptop Image - Original Aspect Ratio */}
        <div className="hidden sm:block w-full overflow-hidden rounded-lg">
          <img 
            src={Images.learnHowItWorks} 
            alt="Learn how it works" 
            className="w-full h-auto object-contain rounded-lg" 
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 -mt-3 sm:mt-0">
          <StepCard
            stepNumber={1}
            title="Receive Consultation Requests"
            description={
              <>
                Cases assigned to you will appear in the <span className="font-semibold">Consultations → Ongoing</span> tab.
              </>
            }
          />
          <StepCard stepNumber={2} title="Review AI Summary & Patient Reports" description="Understand patient data quickly and verify medical details." />
          <StepCard stepNumber={3} title="Submit Your Second Opinion" description="Provide your clinical assessment and recommendations." />
          <StepCard
            stepNumber={4}
            title="Track Completed Cases"
            description={
              <>
                Once submitted, cases move to <span className="font-semibold">Past</span> and then <span className="font-semibold">History</span> for records.
              </>
            }
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl sm:rounded-3xl py-4 sm:py-6 px-4 sm:px-6 lg:px-8 mt-2 sm:mt-4 border border-gray-200 shadow-md">
        <Text variant="headingSemiBold" className="gradient-text text-2xl sm:text-3xl mb-4 sm:mb-6">
          Quick Actions
        </Text>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={handleViewConsultations}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-FigtreeMedium hover:opacity-90 transition-opacity border border-ui_blue bg-ui_bg_pale_blue flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Icon type="consultation" className="w-5 h-5 sm:w-6 sm:h-6" />
              <Text variant="body" className="gradient-text text-sm sm:text-base font-semibold font-FigtreeSemiBold">
                View Consultations
              </Text>
            </div>
            <Icon type="goto" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>

          <button
            onClick={handleGoToProfile}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-FigtreeMedium hover:opacity-90 transition-opacity border border-ui_blue bg-ui_bg_pale_blue flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Icon type="user" className="w-5 h-5 sm:w-6 sm:h-6" />
              <Text variant="body" className="gradient-text text-sm sm:text-base font-semibold font-FigtreeSemiBold">
                Go to Profile
              </Text>
            </div>
            <Icon type="goto" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
